import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CharactersRemaining } from 'components/Form';
import { Button, Form, Input } from 'components/HookForm';
import Message from 'components/Message';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';
import { isVisibleSkuEntity } from '../SkuMenuData.normalizers';
import { SKU_NAME_CHAR_COUNTER_THRESHOLD, SKU_NAME_MAX_INPUT_LENGTH } from '../SkuItems.constants';
import useSkuMutations from '../useSkuMutations';
import { getSkuVenueSource } from '../SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from '../SkuAnalytics.constants';
import AvailabilityToggle from './AvailabilityToggle';
import messages from './EditCategoryModal.messages';
import './EditCategoryModal.scss';

const { block, element } = bem('edit-category-modal');

export const EDIT_CATEGORY_MODAL_NAME = 'EditCategoryModal';

const EditCategoryModal = ({ category, menus, venueId, onSuccess, closeModal, isReadOnly }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const venue = useSelector(selectActiveVenue);
  const source = getSkuVenueSource(venue);
  const { updateCategory } = useSkuMutations({ venueId });
  const [enabled, setEnabled] = useState(category.enabled ?? true);
  const enabledMenus = (menus || []).filter(isVisibleSkuEntity);

  // Each category belongs to exactly one menu via groupId — read-only in edit
  const parentMenu = enabledMenus.find(m => String(m.id) === String(category.groupId));

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={isReadOnly ? messages.viewTitle : messages.title} />
        </h3>
        <button
          type="button"
          aria-label={intl.formatMessage(messages.closeAriaLabel)}
          className={element('close-icon')}
          onClick={closeModal}>
          <Isvg src={ICONS.cross} />
        </button>
      </header>
      <Form id="edit-category-form" defaultValues={{ categoryName: category.name }}>
        {({ formState, handleSubmit, watch }) => {
          const { isDirty } = formState;
          const watchedName = watch('categoryName') || '';
          const isSubmitting = updateCategory.isLoading;
          const enabledChanged = enabled !== (category.enabled ?? true);
          const canSubmit = isDirty || enabledChanged;

          function onSubmit(values) {
            const { categoryName } = values;
            const trimmedName = categoryName.trim();
            updateCategory.mutate(
              {
                categoryId: category.id,
                optimistic: { name: trimmedName, enabled },
                payload: { name: trimmedName, enabled },
              },
              {
                onSuccess: () => {
                  Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_EDITED, { venueId, categoryId: category.id, source });
                  if (enabledChanged) {
                    Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_AVAILABILITY_CHANGED, {
                      venueId,
                      categoryId: category.id,
                      enabled,
                      source,
                    });
                  }
                  dispatch(
                    createSuccessSnackbar(messages.categoryUpdated, {
                      categoryName,
                      strong: chunks => <strong>{chunks}</strong>,
                    })
                  );
                  onSuccess?.();
                  closeModal();
                },
                onError: error => {
                  dispatch(
                    createErrorSnackbar(
                      messages.categoryUpdateError,
                      { strong: chunks => <strong>{chunks}</strong> },
                      { isLowStyle: true },
                      getApiErrorSnackbarDescription(error)
                    )
                  );
                },
              }
            );
          }

          return (
            <>
              <div className={element('form-fields')}>
                <Input
                  name="categoryName"
                  disabled={isReadOnly || isSubmitting}
                  label={messages.categoryNameLabel}
                  type="text"
                  maxLength={SKU_NAME_MAX_INPUT_LENGTH}
                  required
                  showRequiredLabel={!isReadOnly}
                  validate={{
                    message: messages.categoryNameRequired,
                    value: value => Boolean(value?.trim()),
                  }}
                />
                <When
                  predicate={
                    !isReadOnly && SKU_NAME_MAX_INPUT_LENGTH - watchedName.length <= SKU_NAME_CHAR_COUNTER_THRESHOLD
                  }>
                  <div
                    className={element('characters-remaining', {
                      error: watchedName.length >= SKU_NAME_MAX_INPUT_LENGTH,
                    })}>
                    <CharactersRemaining length={SKU_NAME_MAX_INPUT_LENGTH} value={watchedName} showMaxLength />
                  </div>
                </When>
                <div className={element('availability-section')}>
                  <AvailabilityToggle
                    enabled={enabled}
                    onChangeEnabled={setEnabled}
                    isReadOnly={isReadOnly}
                    testId="edit-category-availability-toggle"
                  />
                </div>
                {parentMenu && (
                  <div className={element('menu-section')}>
                    <div className={element('menu-label')}>
                      <Message message={messages.menuLabel} />
                    </div>
                    <div className={element('menu-value')}>{parentMenu.name}</div>
                  </div>
                )}
              </div>
              <footer className={element('footer')}>
                {isReadOnly ? (
                  <Message
                    as={Button}
                    className={element('close-btn')}
                    onClick={closeModal}
                    message={messages.close}
                    styled="primary"
                    type={Button.TYPES.BUTTON}
                  />
                ) : (
                  <>
                    <Message
                      as={Button}
                      className={element('cancel-btn')}
                      onClick={closeModal}
                      message={messages.cancel}
                      styled="primary-outline"
                      type={Button.TYPES.BUTTON}
                    />
                    <Message
                      as={Button}
                      className={element('save-btn')}
                      disabled={!canSubmit || isSubmitting}
                      onClick={handleSubmit(onSubmit)}
                      message={messages.save}
                      styled="primary"
                      type={Button.TYPES.SUBMIT}
                    />
                  </>
                )}
              </footer>
            </>
          );
        }}
      </Form>
    </div>
  );
};

EditCategoryModal.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    groupId: PropTypes.string,
    enabled: PropTypes.bool,
  }).isRequired,
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      enabled: PropTypes.bool,
    })
  ),
  venueId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
};

export default EditCategoryModal;
